import moment from 'moment'

export interface Timeblock {
  id?: number | null
  date: Date | string | null
  starts_at_time: string | null
  ends_at_time: string | null
  employee_id?: number | null
}

export interface CollisionInfo {
  hasCollision: boolean
  startCollides: boolean
  endCollides: boolean
  collidingTasks: any[]
}

/**
 * Checks if two time ranges overlap
 */
export function doTimeRangesOverlap(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean {
  return start1 < end2 && start2 < end1
}

/**
 * Converts a timeblock object to Date objects for start and end times
 */
export function timeblockToDateRange(timeblock: Timeblock): {
  start: Date | null
  end: Date | null
} {
  if (!timeblock.date || !timeblock.starts_at_time || !timeblock.ends_at_time) {
    return { start: null, end: null }
  }

  const dateStr = moment(timeblock.date).format('YYYY-MM-DD')
  const start = moment(`${dateStr} ${timeblock.starts_at_time}`, 'YYYY-MM-DD HH:mm').toDate()
  const end = moment(`${dateStr} ${timeblock.ends_at_time}`, 'YYYY-MM-DD HH:mm').toDate()

  return { start, end }
}

/**
 * Detects if a timeblock collides with any existing tasks
 */
export function detectCollision(
  newTimeblock: Timeblock,
  existingTasks: any[]
): CollisionInfo {
  const result: CollisionInfo = {
    hasCollision: false,
    startCollides: false,
    endCollides: false,
    collidingTasks: [],
  }

  const { start: newStart, end: newEnd } = timeblockToDateRange(newTimeblock)

  if (!newStart || !newEnd) {
    return result
  }

  // Filter tasks to only check timeblocks on the same date
  const tasksOnSameDate = existingTasks.filter((task) => {
    if (!task._real_start) return false
    const taskDate = moment(task._real_start).format('YYYY-MM-DD')
    const newDate = moment(newTimeblock.date).format('YYYY-MM-DD')
    return taskDate === newDate
  })

  for (const task of tasksOnSameDate) {
    // Skip if this is the same timeblock (comparing by ID)
    if (newTimeblock.id && task._timeblock_id && newTimeblock.id === task._timeblock_id) {
      continue
    }

    const taskStart = new Date(task._real_start)
    const taskEnd = new Date(task._real_end)

    if (doTimeRangesOverlap(newStart, newEnd, taskStart, taskEnd)) {
      result.hasCollision = true
      result.collidingTasks.push(task)

      // Check if start time is within an existing task
      if (newStart >= taskStart && newStart < taskEnd) {
        result.startCollides = true
      }

      // Check if end time is within an existing task
      if (newEnd > taskStart && newEnd <= taskEnd) {
        result.endCollides = true
      }

      // Check if the new timeblock completely contains an existing task
      if (newStart <= taskStart && newEnd >= taskEnd) {
        result.startCollides = true
        result.endCollides = true
      }
    }
  }

  return result
}

/**
 * Checks if the start time of a timeblock collides with existing tasks
 */
export function isStartColliding(
  newTimeblock: Timeblock,
  existingTasks: any[]
): boolean {
  const collision = detectCollision(newTimeblock, existingTasks)
  return collision.startCollides
}

/**
 * Checks if the end time of a timeblock collides with existing tasks
 */
export function isEndColliding(
  newTimeblock: Timeblock,
  existingTasks: any[]
): boolean {
  const collision = detectCollision(newTimeblock, existingTasks)
  return collision.endCollides
}

/**
 * Calculates the total duration of a timeblock in hours
 */
export function calculateTimeblockDuration(timeblock: Timeblock): number {
  const { start, end } = timeblockToDateRange(timeblock)
  if (!start || !end) return 0

  const diff = end.getTime() - start.getTime()
  return diff / (1000 * 60 * 60) // Convert to hours
}

/**
 * Get collision segments for timeline visualization
 * Returns an array of time ranges where collisions occur
 */
export function getCollisionSegments(
  newTimeblock: Timeblock,
  existingTasks: any[]
): Array<{ start: Date; end: Date }> {
  const segments: Array<{ start: Date; end: Date }> = []
  const { start: newStart, end: newEnd } = timeblockToDateRange(newTimeblock)

  if (!newStart || !newEnd) {
    return segments
  }

  const tasksOnSameDate = existingTasks.filter((task) => {
    if (!task._real_start) return false
    const taskDate = moment(task._real_start).format('YYYY-MM-DD')
    const newDate = moment(newTimeblock.date).format('YYYY-MM-DD')
    return taskDate === newDate
  })

  for (const task of tasksOnSameDate) {
    // Skip if this is the same timeblock (comparing by ID)
    if (newTimeblock.id && task._timeblock_id && newTimeblock.id === task._timeblock_id) {
      continue
    }

    const taskStart = new Date(task._real_start)
    const taskEnd = new Date(task._real_end)

    if (doTimeRangesOverlap(newStart, newEnd, taskStart, taskEnd)) {
      const overlapStart = new Date(Math.max(newStart.getTime(), taskStart.getTime()))
      const overlapEnd = new Date(Math.min(newEnd.getTime(), taskEnd.getTime()))
      segments.push({ start: overlapStart, end: overlapEnd })
    }
  }

  return segments
}

